/* eslint-disable import/no-cycle */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, generatePath, useLocation } from 'react-router-dom';
import { useInterval } from 'react-interval-hook';
import { AlertSingleLine, Loader } from '@sdir/sds';
import { useIntl } from 'react-intl';
import { NewApplicationLayout } from '../..';
import { routes } from '../../../../constants/routes';
import { useCurrentApplication, useCurrentUserInfo } from '../../../../services/hooks';
import PaymentWindowModule from '../../../Pages/PaymentWindow/PaymentWindowModule';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';

const MAX_RETRY_COUNT = 10;
const RETRY_INTERVAL = 3000;

const Payment: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const pageParams = useParams<{ applicationUid: string }>();
  const location = useLocation();
  const { application, refresh } = useCurrentApplication();
  const [paymentError, setPaymentError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chargeError, setChargeError] = useState<string>();
  const [chargeId, setChargeId] = useState(0);
  const [retryCount, setRetryCount] = useState<number>(0);
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();

  const { start, stop } = useInterval(
    () => {
      const chargeIdParam = new URLSearchParams(location.search)?.get('chargeId');
      if (!chargeId) {
        if (chargeIdParam || application.result?.payment?.chargeId) {
          if (chargeIdParam) {
            setChargeId(+chargeIdParam);
          } else if (application.result?.payment?.chargeId) {
            setChargeId(application.result?.payment?.chargeId);
          }
          stop();
        } else {
          setRetryCount(retryCount + 1);
          refresh?.();
        }
      }
    },
    RETRY_INTERVAL,
    {
      autoStart: false,
      immediate: false,
      selfCorrecting: false,
      onFinish: () => {
        setLoading(false);
        setChargeError(
          retryCount >= MAX_RETRY_COUNT
            ? intl.formatMessage({ id: 'newapp.steps.payment.failed.charges' })
            : undefined
        );
      },
    }
  );

  useEffect(() => {
    if (retryCount >= MAX_RETRY_COUNT) {
      stop();
    }
  }, [retryCount]);

  useEffect(() => {
    if (retryCount === 0) {
      setLoading(true);
      start();
    }
  }, []);

  const goToApplicationSummary = useCallback(() => {
    const applicationSummaryPath = generatePath(routes.application.details, {
      applicationUid: pageParams.applicationUid,
    });
    navigate(`${applicationSummaryPath}?from=paymentstep`);
  }, [navigate, pageParams.applicationUid]);

  const goToApplication = useCallback(() => {
    navigate(
      generatePath(routes.application.details, { applicationUid: pageParams.applicationUid })
    );
  }, [navigate, pageParams.applicationUid]);

  if (loading) {
    return (
      <LoadingWrapper>
        <Loader />
        <LoadingMessage>
          <AlertSingleLine
            title=""
            type="warn"
            text={intl.formatMessage({ id: 'newapp.steps.payment.loadingmessage' })}
          />
        </LoadingMessage>
      </LoadingWrapper>
    );
  }

  return (
    <NewApplicationLayout
      exitLabel={intl.formatMessage({ id: 'general.form.back' })}
      mainContentMaxWidth="100%"
      canNavBack
      navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
      navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
      onNavNextClick={goToApplication}
      onNavBackClick={() => navigate(-1)}
      hideNavigation={!paymentError && !application.result?.payment?.paid}
      canNavNext={!paymentError || application.result?.payment?.paid}
    >
      <Wrapper>
        {chargeError && !chargeId && <AlertSingleLine title="" type="warn" text={chargeError} />}
        {!application.loading && !chargeError && (
          <PaymentWindowModule
            errorCallback={setPaymentError}
            cancelCallback={goToApplication}
            successCallback={goToApplicationSummary}
            chargeId={chargeId}
            isExternalUser={isExternal}
          />
        )}
      </Wrapper>
    </NewApplicationLayout>
  );
};

export default Payment;

const Wrapper = styled.div`
  width: 100%;
  display: grid;
  padding: 3rem 0;
  grid-template-columns: 50% 40%;
  column-gap: 3rem;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    grid-template-columns: 1fr;
    row-gap: 3rem;
  }
`;

const LoadingWrapper = styled.div`
  margin-top: 40px;
`;

const LoadingMessage = styled.div`
  margin: 40px auto 0 auto;
  max-width: 500px;
`;
